import React, { useState } from 'react';
import GradientText from '../components/GradientText';
import { PremiumBackground } from '../components/EnhancedBackground';
import { FloatingOrbs, AnimatedGrid, AnimatedTextReveal, PremiumCard } from '../components/PremiumEffects';
import { ArrowRight, CheckCircle, Calendar, Mail, Phone, Building2 } from 'lucide-react';
import { leadAPI } from '../services/api';

const steps = [
  { title: 'Tell us about you', desc: 'Share a few basics so we can tailor the conversation.' },
  { title: 'Pick a focus', desc: 'Select outcomes you want: revenue, cost, efficiency, CX.' },
  { title: 'Book a slot', desc: 'Choose a time and we’ll prepare a mini‑plan for your call.' }
];

const quickOutcomes = [
  'Increase Revenue', 'Reduce Costs', 'Improve Efficiency', 'Enhance Customer Experience', 'Accelerate Decisions', 'AI‑Powered Apps'
];

const GetStarted = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', outcomes: [] });
  const [submitting, setSubmitting] = useState(false);

  const toggleOutcome = (label) => {
    setForm((f) => ({
      ...f,
      outcomes: f.outcomes.includes(label) ? f.outcomes.filter(o => o !== label) : [...f.outcomes, label]
    }));
  };

  const submit = async () => {
    if (!form.email) {
      alert('Please enter your email address.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await leadAPI.submit({
        name: form.name,
        email: form.email,
        phone: form.phone,
        company: form.company,
        outcomes: form.outcomes,
        preferredTime: 'ASAP'
      });
      alert(result.message || 'Thanks! We’ll be in touch shortly.');
      setForm({ name: '', email: '', phone: '', company: '', outcomes: [] });
    } catch (e) {
      alert(e.message || 'Could not submit right now. Please try again.');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 custom-scrollbar text-white">
      <PremiumBackground />
      <FloatingOrbs />
      <AnimatedGrid />

      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-24 text-center">
        <AnimatedTextReveal>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">Get <GradientText>Started</GradientText></h1>
          <p className="text-gray-300 max-w-2xl mx-auto">We’ll scope your goals, recommend the fastest value path, and set you up for a quick, high‑confidence win.</p>
        </AnimatedTextReveal>
      </section>

      {/* Steps + Form */}
      <section className="section-padding">
        <div className="container-custom grid gap-8 md:grid-cols-3">
          {/* Left: Steps & Outcomes */}
          <div className="md:col-span-2 space-y-8">
            <div className="grid md:grid-cols-3 gap-4">
              {steps.map((s, i) => (
                <PremiumCard key={s.title} className={`h-full ${activeStep === i ? 'ring-2 ring-purple-400/40' : ''}`}>
                  <div className="text-sm text-purple-300 mb-1">Step {i + 1}</div>
                  <div className="text-xl font-semibold mb-1">{s.title}</div>
                  <div className="text-sm text-gray-300">{s.desc}</div>
                </PremiumCard>
              ))}
            </div>

            <PremiumCard>
              <div className="mb-4 font-semibold">What outcomes are you targeting?</div>
              <div className="flex flex-wrap gap-2">
                {quickOutcomes.map((o) => (
                  <button
                    key={o}
                    onClick={() => toggleOutcome(o)}
                    className={`px-3 py-1 rounded-full text-sm border ${form.outcomes.includes(o) ? 'bg-purple-600/30 border-purple-400 text-white' : 'bg-white/5 border-white/10 text-gray-300'}`}
                  >
                    {form.outcomes.includes(o) && <CheckCircle className="w-4 h-4 inline mr-1 text-green-400" />}{o}
                  </button>
                ))}
              </div>
            </PremiumCard>
          </div>

          {/* Right: Contact Card */}
          <PremiumCard>
            <div className="space-y-4">
              <div className="text-lg font-semibold">Share your details</div>
              <div className="space-y-3">
                <div className="flex items-center bg-white/10 border border-white/10 rounded-lg px-3 py-2">
                  <Building2 className="w-4 h-4 mr-2 text-gray-300" />
                  <input value={form.company} onChange={(e)=>setForm({...form,company:e.target.value})} placeholder="Company" className="w-full bg-transparent outline-none placeholder-gray-400" />
                </div>
                <div className="flex items-center bg-white/10 border border-white/10 rounded-lg px-3 py-2">
                  <Mail className="w-4 h-4 mr-2 text-gray-300" />
                  <input value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} placeholder="Work Email" className="w-full bg-transparent outline-none placeholder-gray-400" />
                </div>
                <div className="flex items-center bg-white/10 border border-white/10 rounded-lg px-3 py-2">
                  <Phone className="w-4 h-4 mr-2 text-gray-300" />
                  <input value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} placeholder="Phone (optional)" className="w-full bg-transparent outline-none placeholder-gray-400" />
                </div>
                <div className="flex items-center bg-white/10 border border-white/10 rounded-lg px-3 py-2">
                  <Calendar className="w-4 h-4 mr-2 text-gray-300" />
                  <select className="w-full bg-transparent outline-none">
                    <option className="bg-gray-900">Preferred time: ASAP</option>
                    <option className="bg-gray-900">This week</option>
                    <option className="bg-gray-900">Next week</option>
                  </select>
                </div>
              </div>
              <button onClick={submit} className="w-full px-5 py-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg font-semibold inline-flex items-center justify-center gap-2">
                <ArrowRight className="w-5 h-5" /> Request Free Discovery Call
              </button>
              <div className="text-xs text-gray-400">We’ll reply within 24 hours. No spam, no sales pressure.</div>
            </div>
          </PremiumCard>
        </div>
      </section>
    </div>
  );
};

export default GetStarted;


